import React, { useEffect, useRef, useCallback, useState } from "react";
import "../styles/executive-responsive.css";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, CalendarDays } from "lucide-react";
import { CalendarPicker } from "./CalendarPicker";

export interface DayInfo {
  iso: string;
  date: Date;
  isToday: boolean;
  dayNumber: number;
}

interface DateCarouselProps {
  recordingDays: DayInfo[];
  selectedDate: string | null;
  onSelectDate: (dateIso: string | null) => void;
}

export const DateCarousel: React.FC<DateCarouselProps> = ({
  recordingDays,
  selectedDate,
  onSelectDate,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const selectedIndex = recordingDays.findIndex(
    (day) => (day.isToday && !selectedDate) || day.iso === selectedDate,
  );
  const activeIdx = selectedIndex === -1 ? recordingDays.length - 1 : selectedIndex;

  const handleCalendarSelect = useCallback(
    (val: string) => {
      const match = recordingDays.find((d) => d.iso === val);
      if (match) {
        onSelectDate(match.isToday ? null : match.iso);
      } else {
        onSelectDate(val);
      }
    },
    [recordingDays, onSelectDate],
  );

  useEffect(() => {
    // Use a small timeout to ensure layout is ready
    setTimeout(() => {
      if (buttonRefs.current[activeIdx]) {
        buttonRefs.current[activeIdx]?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }, 50);
  }, [selectedDate, recordingDays.length, activeIdx]);

  const minDate = recordingDays.length > 0 ? recordingDays[0].iso : undefined;
  const maxDate =
    recordingDays.length > 0 ? recordingDays[recordingDays.length - 1].iso : undefined;

  return (
    <div
      className="date-carousel-wrapper"
      style={{
        position: "relative",
        maxWidth: "860px",
        margin: "0 auto",
        marginBottom: "32px",
        display: "flex",
        alignItems: "center",
        gap: "8px",
      }}
    >
      {/* First Day Button */}
      <button
        onClick={() => {
          if (activeIdx > 0) {
            const firstDay = recordingDays[0];
            onSelectDate(firstDay.isToday ? null : firstDay.iso);
          }
        }}
        disabled={activeIdx === 0}
        style={{
          background: "transparent",
          border: "none",
          width: "32px",
          height: "40px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: activeIdx === 0 ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.4)",
          cursor: activeIdx === 0 ? "default" : "pointer",
          transition: "all 0.2s",
          flexShrink: 0,
          zIndex: 10,
        }}
        onMouseEnter={(e) => {
          if (activeIdx !== 0) e.currentTarget.style.color = "#fff";
        }}
        onMouseLeave={(e) => {
          if (activeIdx !== 0) e.currentTarget.style.color = "rgba(255,255,255,0.4)";
        }}
      >
        <ChevronsLeft size={20} />
      </button>

      {/* Prev Button */}
      <button
        onClick={() => {
          if (activeIdx > 0) {
            const prevDay = recordingDays[activeIdx - 1];
            onSelectDate(prevDay.isToday ? null : prevDay.iso);
          }
        }}
        disabled={activeIdx === 0}
        style={{
          background: "rgba(255,255,255,0.05)",
          border: "none",
          borderRadius: "50%",
          width: "40px",
          height: "40px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: activeIdx === 0 ? "rgba(255,255,255,0.2)" : "#fff",
          cursor: activeIdx === 0 ? "default" : "pointer",
          transition: "all 0.2s",
          flexShrink: 0,
          zIndex: 10,
        }}
        onMouseEnter={(e) => {
          if (activeIdx !== 0) e.currentTarget.style.background = "rgba(236,72,153,0.2)";
        }}
        onMouseLeave={(e) => {
          if (activeIdx !== 0) e.currentTarget.style.background = "rgba(255,255,255,0.05)";
        }}
      >
        <ChevronLeft size={20} />
      </button>

      <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>
        <div
          ref={scrollContainerRef}
          style={{
            display: "flex",
            overflowX: "auto",
            gap: "8px",
            paddingBottom: "16px",
            paddingTop: "16px",
            paddingLeft: "calc(50% - 65px)",
            paddingRight: "calc(50% - 65px)",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            WebkitMaskImage:
              "linear-gradient(to right, transparent, black 15%, black 85%, transparent)",
            maskImage: "linear-gradient(to right, transparent, black 15%, black 85%, transparent)",
            scrollBehavior: "smooth",
          }}
        >
          {recordingDays.map((day, idx) => {
            const isActive = day.isToday ? !selectedDate : selectedDate === day.iso;
            const scale = isActive ? 1 : 0.9;
            const opacity = isActive ? 1 : 0.5;

            return (
              <button
                key={day.iso}
                className="date-carousel-item"
                ref={(el) => {
                  buttonRefs.current[idx] = el;
                }}
                onClick={() => onSelectDate(day.isToday ? null : day.iso)}
                style={{
                  flexShrink: 0,
                  minWidth: "110px",
                  background: isActive ? "rgba(236,72,153,0.15)" : "transparent",
                  border: `1px solid ${isActive ? "#EC4899" : day.isToday ? "rgba(236,72,153,0.3)" : "rgba(255,255,255,0.05)"}`,
                  borderRadius: "12px",
                  padding: "16px 8px",
                  cursor: "pointer",
                  textAlign: "center",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  position: "relative",
                  transform: `scale(${scale})`,
                  opacity: opacity,
                  boxShadow: isActive ? "0 4px 12px rgba(236,72,153,0.15)" : "none",
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.opacity = "0.8";
                    e.currentTarget.style.transform = "scale(0.95)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.opacity = "0.5";
                    e.currentTarget.style.transform = "scale(0.9)";
                  }
                }}
              >
                {day.isToday && (
                  <div
                    style={{
                      position: "absolute",
                      top: "8px",
                      right: "8px",
                      width: "6px",
                      height: "6px",
                      backgroundColor: "#EC4899",
                      borderRadius: "50%",
                      boxShadow: "0 0 6px rgba(236,72,153,0.8)",
                    }}
                  />
                )}
                <div
                  style={{
                    fontSize: "0.7rem",
                    color: isActive || day.isToday ? "#EC4899" : "var(--color-text-muted)",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    marginBottom: "4px",
                    letterSpacing: "0.5px",
                  }}
                >
                  {day.isToday
                    ? "Hoy"
                    : day.date.toLocaleDateString("es-MX", {
                        weekday: "short",
                        timeZone: "America/Mexico_City",
                      })}
                </div>
                <div
                  style={{
                    fontSize: "1.2rem",
                    fontWeight: 800,
                    color: isActive ? "#fff" : "var(--color-text-primary)",
                    marginBottom: "4px",
                  }}
                >
                  {day.date
                    .toLocaleDateString("es-MX", {
                      day: "numeric",
                      month: "short",
                      timeZone: "America/Mexico_City",
                    })
                    .toUpperCase()}
                </div>
                <div
                  style={{
                    fontSize: "0.65rem",
                    color: isActive ? "rgba(255,255,255,0.7)" : "var(--color-text-muted)",
                    fontWeight: 600,
                  }}
                >
                  Día {day.dayNumber}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Next Button */}
      <button
        onClick={() => {
          if (activeIdx < recordingDays.length - 1) {
            const nextDay = recordingDays[activeIdx + 1];
            onSelectDate(nextDay.isToday ? null : nextDay.iso);
          }
        }}
        disabled={activeIdx === recordingDays.length - 1}
        style={{
          background: "rgba(255,255,255,0.05)",
          border: "none",
          borderRadius: "50%",
          width: "40px",
          height: "40px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: activeIdx === recordingDays.length - 1 ? "rgba(255,255,255,0.2)" : "#fff",
          cursor: activeIdx === recordingDays.length - 1 ? "default" : "pointer",
          transition: "all 0.2s",
          flexShrink: 0,
          zIndex: 10,
        }}
        onMouseEnter={(e) => {
          if (activeIdx !== recordingDays.length - 1)
            e.currentTarget.style.background = "rgba(236,72,153,0.2)";
        }}
        onMouseLeave={(e) => {
          if (activeIdx !== recordingDays.length - 1)
            e.currentTarget.style.background = "rgba(255,255,255,0.05)";
        }}
      >
        <ChevronRight size={20} />
      </button>

      {/* Last Day Button */}
      <button
        onClick={() => {
          if (activeIdx < recordingDays.length - 1) {
            const lastDay = recordingDays[recordingDays.length - 1];
            onSelectDate(lastDay.isToday ? null : lastDay.iso);
          }
        }}
        disabled={activeIdx === recordingDays.length - 1}
        style={{
          background: "transparent",
          border: "none",
          width: "32px",
          height: "40px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color:
            activeIdx === recordingDays.length - 1
              ? "rgba(255,255,255,0.1)"
              : "rgba(255,255,255,0.4)",
          cursor: activeIdx === recordingDays.length - 1 ? "default" : "pointer",
          transition: "all 0.2s",
          flexShrink: 0,
          zIndex: 10,
        }}
        onMouseEnter={(e) => {
          if (activeIdx !== recordingDays.length - 1) e.currentTarget.style.color = "#fff";
        }}
        onMouseLeave={(e) => {
          if (activeIdx !== recordingDays.length - 1)
            e.currentTarget.style.color = "rgba(255,255,255,0.4)";
        }}
      >
        <ChevronsRight size={20} />
      </button>

      {/* Calendar picker button */}
      <div style={{ flexShrink: 0, position: "relative" }}>
        <button
          onClick={() => setCalendarOpen((o) => !o)}
          title="Ir a fecha"
          style={{
            background: calendarOpen ? "rgba(236,72,153,0.25)" : "rgba(236,72,153,0.1)",
            border: `1px solid ${calendarOpen ? "#EC4899" : "rgba(236,72,153,0.3)"}`,
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#EC4899",
            cursor: "pointer",
            transition: "all 0.2s",
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(236,72,153,0.25)";
            e.currentTarget.style.borderColor = "#EC4899";
          }}
          onMouseLeave={(e) => {
            if (!calendarOpen) {
              e.currentTarget.style.background = "rgba(236,72,153,0.1)";
              e.currentTarget.style.borderColor = "rgba(236,72,153,0.3)";
            }
          }}
        >
          <CalendarDays size={18} />
        </button>
        {calendarOpen && (
          <CalendarPicker
            value={selectedDate}
            onChange={handleCalendarSelect}
            onClose={() => setCalendarOpen(false)}
            minDate={minDate}
            maxDate={maxDate}
          />
        )}
      </div>
    </div>
  );
};
